class CacheService {
    private cache: Map<string, { value: any; expiresAt: number | null }>;
    private timeouts: Map<string, NodeJS.Timeout>;

    constructor() {
        this.cache = new Map();
        this.timeouts = new Map();
    }

    set(key: string | number, value: any, ttl: number | null = null): void {
        const stringKey = String(key);

        if (this.timeouts.has(stringKey)) {
            clearTimeout(this.timeouts.get(stringKey));
            this.timeouts.delete(stringKey);
        }

        this.cache.set(stringKey, {
            value,
            expiresAt: ttl ? Date.now() + ttl : null
        });

        if (ttl) {
            const timeoutId = setTimeout(() => {
                this.cache.delete(stringKey);
                this.timeouts.delete(stringKey);
            }, ttl);

            this.timeouts.set(stringKey, timeoutId);
        }
    }

    get<T = any>(key: string | number): T | null {
        const stringKey = String(key);
        const entry = this.cache.get(stringKey);

        if (!entry) {
            return null;
        }

        if (entry.expiresAt && Date.now() > entry.expiresAt) {
            this.cache.delete(stringKey);
            const timeoutId = this.timeouts.get(stringKey);
            if (timeoutId) {
                clearTimeout(timeoutId);
                this.timeouts.delete(stringKey);
            }
            return null;
        }

        return entry.value as T;
    }

    invalidate(key: string | number): void {
        const stringKey = String(key);

        const timeoutId = this.timeouts.get(stringKey);
        if (timeoutId) {
            clearTimeout(timeoutId);
            this.timeouts.delete(stringKey);
        }

        this.cache.delete(stringKey);
    }

    clear(): void {
        this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
        this.timeouts.clear();
        this.cache.clear();
    }
}

const cacheServiceInstance = new CacheService();
export default cacheServiceInstance;
